import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNotifications } from "../../contexts/NotificationContext";

type Position = {
	x: number;
	y: number;
};

type Direction = "UP" | "DOWN" | "LEFT" | "RIGHT";

const GRID_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];

const Snake = () => {
	const { addNotification } = useNotifications();
	const [isPlaying, setIsPlaying] = useState(false);
	const [score, setScore] = useState(0);
	const [snake, setSnake] = useState<Position[]>(INITIAL_SNAKE);
	const [food, setFood] = useState<Position>({ x: 15, y: 15 });
	const [direction, setDirection] = useState<Direction>("RIGHT");
	const [gameSpeed, setGameSpeed] = useState(150);

	const generateFood = useCallback(() => {
		const newFood = {
			x: Math.floor(Math.random() * GRID_SIZE),
			y: Math.floor(Math.random() * GRID_SIZE),
		};
		// Evita gerar comida sobre a cobra
		if (snake.some((segment) => segment.x === newFood.x && segment.y === newFood.y)) {
			return generateFood();
		}
		return newFood;
	}, [snake]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!isPlaying) return;

			switch (e.key) {
				case "ArrowUp":
					if (direction !== "DOWN") setDirection("UP");
					break;
				case "ArrowDown":
					if (direction !== "UP") setDirection("DOWN");
					break;
				case "ArrowLeft":
					if (direction !== "RIGHT") setDirection("LEFT");
					break;
				case "ArrowRight":
					if (direction !== "LEFT") setDirection("RIGHT");
					break;
			}
		},
		[isPlaying, direction]
	);

	const checkCollision = useCallback(
		(head: Position) => {
			// Colisão com as paredes
			if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
				return true;
			}

			// Colisão com o próprio corpo
			return snake.some((segment) => segment.x === head.x && segment.y === head.y);
		},
		[snake]
	);

	// Game loop
	useEffect(() => {
		if (!isPlaying) return;

		const gameLoop = setInterval(() => {
			setSnake((prevSnake) => {
				const head = { ...prevSnake[0] };

				switch (direction) {
					case "UP":
						head.y -= 1;
						break;
					case "DOWN":
						head.y += 1;
						break;
					case "LEFT":
						head.x -= 1;
						break;
					case "RIGHT":
						head.x += 1;
						break;
				}

				if (checkCollision(head)) {
					setIsPlaying(false);
					addNotification({
						message: `Game Over! Score: ${score}`,
						type: "info",
						icon: "🐍",
					});
					return prevSnake;
				}

				const newSnake = [head, ...prevSnake];

				// Comeu a comida
				if (head.x === food.x && head.y === food.y) {
					setScore((prev) => prev + 100);
					setFood(generateFood());
					setGameSpeed((prev) => Math.max(prev * 0.95, 50));
				} else {
					newSnake.pop();
				}

				return newSnake;
			});
		}, gameSpeed);

		return () => clearInterval(gameLoop);
	}, [
		isPlaying,
		direction,
		food,
		generateFood,
		checkCollision,
		score,
		gameSpeed,
		addNotification,
	]);

	useEffect(() => {
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	const startGame = () => {
		setIsPlaying(true);
		setSnake(INITIAL_SNAKE);
		setDirection("RIGHT");
		setScore(0);
		setGameSpeed(150);
		setFood(generateFood());
	};

	return (
		<div className='relative h-96 bg-black/30 rounded-xl overflow-hidden'>
			{!isPlaying ? (
				<button
					onClick={startGame}
					className='absolute inset-0 flex flex-col items-center justify-center text-trump-yellow font-bold'>
					<span className='text-4xl mb-2'>🐍</span>
					<span className='text-xl'>START SNAKE</span>
					<span className='text-sm text-white/60 mt-2'>
						Use arrow keys to control the snake!
					</span>
				</button>
			) : (
				<>
					<div className='absolute top-2 left-2 bg-black/50 px-4 py-2 rounded-full text-trump-yellow font-bold'>
						Score: {score}
					</div>
					<div
						className='absolute inset-0 grid'
						style={{
							gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
						}}>
						{/* Snake */}
						{snake.map((segment, index) => (
							<motion.div
								key={`${segment.x}-${segment.y}-${index}`}
								className='bg-trump-yellow rounded-sm'
								style={{
									gridColumn: segment.x + 1,
									gridRow: segment.y + 1,
									margin: "1px",
								}}
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
							/>
						))}
						{/* Food */}
						<motion.div
							className='bg-trump-red rounded-full'
							style={{
								gridColumn: food.x + 1,
								gridRow: food.y + 1,
								margin: "2px",
							}}
							animate={{
								scale: [1, 1.2, 1],
							}}
							transition={{
								duration: 0.5,
								repeat: Infinity,
							}}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default Snake;
